import React, {useEffect, useState} from 'react';
import Modal from '../workspace/Modal';
import {Project} from '../../domain/types';
import {StarIcon} from '../Icons';

interface SetDefaultWordlistModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (projectId: string) => void;
  isConfirming: boolean;
  wordlistName: string;
  projects: Project[];
  currentProjectId?: string | null;
}

/**
 * Which project should reach for this list first.
 *
 * A project has exactly one default, so this dialog's real job is showing what it is about to
 * replace — the previous default is named before the button is pressed, not after.
 */
export const SetDefaultWordlistModal: React.FC<SetDefaultWordlistModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isConfirming,
  wordlistName,
  projects,
  currentProjectId,
}) => {
  const [selectedProjectId, setSelectedProjectId] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedProjectId(currentProjectId ?? '');
    }
  }, [isOpen, currentProjectId]);

  if (!isOpen) {
    return null;
  }

  const selectedProject = projects.find(project => project.id === selectedProjectId);

  return (
    <Modal
      isOpen
      title="Set as default word list"
      subtitle={<>Conversions in the chosen project will start with “{wordlistName}” selected.</>}
      onClose={isConfirming ? () => {} : onClose}
      widthClass="max-w-md"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={isConfirming}
            className="rounded-lg px-3 py-1.5 text-sm text-gray-400 hover:bg-gray-800 hover:text-gray-100
             transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(selectedProjectId)}
            disabled={!selectedProjectId || isConfirming}
            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm
             font-medium text-white hover:bg-indigo-500 transition-colors
             disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
          >
            <StarIcon className="w-4 h-4" />
            {isConfirming ? 'Setting…' : 'Set as default'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label htmlFor="default-wordlist-project" className="mb-1 block text-xs font-medium text-gray-300">
            Project
          </label>
          <select
            id="default-wordlist-project"
            value={selectedProjectId}
            onChange={event => setSelectedProjectId(event.target.value)}
            disabled={isConfirming || projects.length === 0}
            className="w-full rounded-lg border border-gray-800 bg-gray-950/60 px-3 py-2 text-sm
             text-gray-100 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500
             disabled:opacity-50"
          >
            <option value="">Select a project…</option>
            {projects.map(project => (
              <option key={project.id} value={project.id}>{project.name}</option>
            ))}
          </select>
        </div>

        {selectedProject && (
          <p className="text-xs text-gray-500">
            {selectedProject.defaultWordlistName ? (
              <>
                <span className="text-amber-300">{selectedProject.defaultWordlistName}</span> is its
                default today and will be replaced. A project can only have one.
              </>
            ) : (
              <>This project has no default word list yet.</>
            )}
          </p>
        )}
      </div>
    </Modal>
  );
};

export default SetDefaultWordlistModal;
